/**
 * The cards the add-a-machine page renders, one per pairing state
 * (forge#3520). Extracted from the page so the orchestrator stays a state
 * machine and every card stays a prop-driven renderer; extracted into this
 * file so the page stays under the god-file line.
 */

import { Link2Icon } from "lucide-react";

import { Button } from "../ui/button";
import { QRCodeSvg } from "../ui/qr-code";
import { Spinner } from "../ui/spinner";

const CARD_CLASS = "rounded-[10px] border border-border/70 bg-card";
const PENDING_CARD_CLASS =
  "flex min-h-[252px] w-[292px] flex-col items-center justify-center gap-3 rounded-[10px] border border-border/70 bg-card px-6 text-sm";

export function PairMachineLoadingCard() {
  return (
    <div
      className={`${CARD_CLASS} flex min-h-[252px] w-[252px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground`}
    >
      <Spinner className="size-5" />
      <span>Creating pairing link...</span>
    </div>
  );
}

/** Shown while the mint-time client-session baseline is being established. A
 * link is never minted (and linked is never evaluated) until this succeeds. */
export function PairMachineCheckingCard() {
  return (
    <div
      className={`${CARD_CLASS} flex min-h-[252px] w-[252px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground`}
    >
      <Spinner className="size-5" />
      <span>Checking your connections...</span>
    </div>
  );
}

export function PairMachineErrorCard({ message }: { readonly message: string }) {
  return (
    <p className={`${CARD_CLASS} max-w-xs px-6 py-6 text-sm text-muted-foreground`} role="alert">
      {message}
    </p>
  );
}

export function PairMachineLinkedCard({
  onViewEnvironments,
}: {
  readonly onViewEnvironments: () => void;
}) {
  return (
    <div className={`${PENDING_CARD_CLASS} min-h-[252px]`}>
      <p className="font-medium text-foreground">Machine linked</p>
      <p className="text-muted-foreground">
        It now appears in your environments list with live status.
      </p>
      <Button type="button" size="sm" onClick={onViewEnvironments}>
        View environments
      </Button>
    </div>
  );
}

export function PairMachineRevokedCard() {
  return (
    <div className={`${PENDING_CARD_CLASS} min-h-[252px]`}>
      <p className="font-medium text-foreground">Link revoked</p>
      <p className="text-muted-foreground">The URL and QR code no longer work anywhere.</p>
      <Button type="button" size="sm" onClick={() => window.location.reload()}>
        Create a new link
      </Button>
    </div>
  );
}

export function PairMachineReadyCard({
  pairingUrl,
  expiresLabel,
  restored,
}: {
  readonly pairingUrl: string;
  readonly expiresLabel: string | null;
  readonly restored: boolean;
}) {
  return (
    <div className="flex flex-col items-center">
      <div className={`${CARD_CLASS} p-3.5 shadow-sm`}>
        <QRCodeSvg
          value={pairingUrl}
          size={220}
          level="M"
          marginSize={2}
          title="Machine pairing QR code"
        />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {expiresLabel ? `Expires at ${expiresLabel}` : "Pairing link ready"} ·{" "}
        {restored ? "this tab's pending link" : "waiting for the machine to sign in"}
      </p>
    </div>
  );
}

export function PairMachineReadyActions({
  pairingUrl,
  copyToClipboard,
  isCopied,
  onRevoke,
}: {
  readonly pairingUrl: string;
  readonly copyToClipboard: (value: string) => void;
  readonly isCopied: boolean;
  readonly onRevoke: () => void;
}) {
  return (
    <>
      <div className="mt-4 flex w-full items-center gap-2">
        <input
          type="text"
          readOnly
          aria-label="Pairing URL"
          value={pairingUrl}
          className="h-8 min-w-0 flex-1 rounded-md border border-border/60 bg-card px-2 text-xs text-foreground focus:outline-none"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard(pairingUrl)}
        >
          <Link2Icon aria-hidden="true" className="size-3.5" />
          {isCopied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="mt-3 max-w-[320px] text-[13px] leading-5 text-muted-foreground">
        Open this link on the machine to add, or scan the code. The link works once and expires on
        its own.
      </p>
      <Button type="button" variant="ghost" size="sm" className="mt-3" onClick={onRevoke}>
        Revoke link
      </Button>
    </>
  );
}
